import { INSTRUCTION_HANDBOOK_SECTION_IDS } from './instructionHandbookSections'
import type {
  ComplianceDocumentSectionKind,
  ComplianceDocumentSectionRecord,
} from './complianceDocumentSectionRecord'
import {
  parseMarkdownHeadingLine,
  stripRedundantSectionHeadingFromBody,
} from './stripRedundantSectionHeadingFromBody'
import { unwrapOuterMarkdownCodeFence } from './unwrapOuterMarkdownCodeFence'

export type InstructionHandbookSectionKind = ComplianceDocumentSectionKind

export type InstructionHandbookSectionRecord = ComplianceDocumentSectionRecord

const CANONICAL_SECTION_ID_SET = new Set<string>(INSTRUCTION_HANDBOOK_SECTION_IDS)

export function isCanonicalInstructionHandbookSectionId(
  sectionId: string
): boolean {
  return CANONICAL_SECTION_ID_SET.has(sectionId)
}

function inferSectionKind(sectionId: string): InstructionHandbookSectionKind {
  return isCanonicalInstructionHandbookSectionId(sectionId)
    ? 'canonical'
    : 'custom'
}

/** User-authored appendix ids (`appendix-1`, `appendix-a`, …). */
export function isCustomInstructionHandbookAppendixSectionId(
  sectionId: string
): boolean {
  return /^appendix-/i.test(sectionId)
}

function isTopLevelInstructionHandbookSectionHeading(
  sectionId: string
): boolean {
  return (
    isCanonicalInstructionHandbookSectionId(sectionId) ||
    isCustomInstructionHandbookAppendixSectionId(sectionId)
  )
}

export function isInstructionHandbookChildSectionRecord(section: {
  sectionId: string
  parentSectionId?: string | null
}): boolean {
  return (
    section.parentSectionId != null && section.parentSectionId.length > 0
  )
}

function parseHeadingLine(line: string): { sectionId: string; title: string } | null {
  if (!line.startsWith('## ')) {
    return null
  }
  const rest = line.slice(3).trimStart()
  const spaceIndex = rest.indexOf(' ')
  if (spaceIndex === -1) {
    return { sectionId: rest, title: '' }
  }
  return {
    sectionId: rest.slice(0, spaceIndex),
    title: rest.slice(spaceIndex + 1).trim(),
  }
}

/** @deprecated Use `stripRedundantSectionHeadingFromBody` from the package root. */
export function stripInstructionHandbookRedundantSectionHeadingFromBody(
  markdown: string,
  sectionId: string,
  title: string
): string {
  return stripRedundantSectionHeadingFromBody(markdown, sectionId, title)
}

function stripOrchestratorSectionHeadingFromGeneratedMarkdown(
  sectionId: string,
  generatedMarkdown: string,
  options?: { parentSectionId?: string | null }
): string {
  const lines = generatedMarkdown.split('\n')
  let index = 0

  while (index < lines.length && lines[index].trim() === '') {
    index += 1
  }

  if (index >= lines.length) {
    return generatedMarkdown.trim()
  }

  const heading = parseHeadingLine(lines[index])
  if (heading?.sectionId === sectionId) {
    index += 1
    while (index < lines.length && lines[index].trim() === '') {
      index += 1
    }
    return lines.slice(index).join('\n').trim()
  }

  if (options?.parentSectionId != null) {
    const childHeading = parseMarkdownHeadingLine(lines[index])
    if (childHeading != null && childHeading.level === 3) {
      index += 1
      while (index < lines.length && lines[index].trim() === '') {
        index += 1
      }
      return lines.slice(index).join('\n').trim()
    }
  }

  return generatedMarkdown.trim()
}

export function buildInstructionHandbookSectionRecordFromOrchestratorChunk(options: {
  sectionId: string
  parentSectionId?: string | null
  title: string
  generatedMarkdown: string
  sortOrder: number
}): InstructionHandbookSectionRecord {
  const bodyWithoutChromeHeading =
    stripOrchestratorSectionHeadingFromGeneratedMarkdown(
      options.sectionId,
      options.generatedMarkdown,
      { parentSectionId: options.parentSectionId }
    )

  return normalizeInstructionHandbookSectionRecord({
    sectionId: options.sectionId,
    parentSectionId: options.parentSectionId ?? null,
    title: options.title,
    kind: inferSectionKind(options.sectionId),
    sortOrder: options.sortOrder,
    markdown: bodyWithoutChromeHeading,
  })
}

export function normalizeInstructionHandbookSectionRecord(
  section: InstructionHandbookSectionRecord
): InstructionHandbookSectionRecord {
  const markdown = unwrapOuterMarkdownCodeFence(
    stripInstructionHandbookRedundantSectionHeadingFromBody(
      section.markdown,
      section.sectionId,
      section.title
    )
  )

  return {
    ...section,
    parentSectionId: section.parentSectionId ?? null,
    markdown,
  }
}

export function normalizeInstructionHandbookSections(
  sections: InstructionHandbookSectionRecord[]
): InstructionHandbookSectionRecord[] {
  return sections.map(normalizeInstructionHandbookSectionRecord)
}

/**
 * Split markdown on line-start `## ` headings into preamble + section records.
 * For tests/tooling only — not used as read-path backfill.
 */
export function parseInstructionHandbookSections(markdown: string): {
  preamble: string
  sections: InstructionHandbookSectionRecord[]
} {
  const lines = markdown.split('\n')
  let index = 0
  const preambleLines: string[] = []

  while (index < lines.length) {
    const line = lines[index]
    if (line.startsWith('## ')) {
      break
    }
    preambleLines.push(line)
    index += 1
  }

  const sections: InstructionHandbookSectionRecord[] = []
  let sortOrder = 0

  while (index < lines.length) {
    const line = lines[index]
    if (!line.startsWith('## ')) {
      index += 1
      continue
    }

    const heading = parseHeadingLine(line)
    if (heading == null || heading.sectionId === '') {
      index += 1
      continue
    }

    const bodyLines: string[] = []
    index += 1
    while (index < lines.length && !lines[index].startsWith('## ')) {
      bodyLines.push(lines[index])
      index += 1
    }

    if (!isTopLevelInstructionHandbookSectionHeading(heading.sectionId)) {
      const previousIndex = sections.length - 1
      const previousSection =
        previousIndex >= 0 ? sections[previousIndex] : undefined
      if (previousSection != null) {
        const foldedBlock = [line, ...bodyLines].join('\n').trim()
        previousSection.markdown =
          previousSection.markdown.trim().length > 0
            ? `${previousSection.markdown.trimEnd()}\n\n${foldedBlock}`
            : foldedBlock
        continue
      }
    }

    sections.push(
      normalizeInstructionHandbookSectionRecord({
        sectionId: heading.sectionId,
        parentSectionId: null,
        title: heading.title,
        kind: inferSectionKind(heading.sectionId),
        sortOrder,
        markdown: bodyLines.join('\n').trim(),
      })
    )
    sortOrder += 1
  }

  return {
    preamble: preambleLines.join('\n').trimEnd(),
    sections,
  }
}

export function formatInstructionHandbookSectionBlock(
  section: InstructionHandbookSectionRecord,
): string {
  if (section.parentSectionId != null && section.parentSectionId.length > 0) {
    const heading =
      section.title.trim().length > 0
        ? `### ${section.sectionId} ${section.title}`
        : `### ${section.sectionId}`
    const body = section.markdown.trimEnd()
    if (body.length === 0) {
      return heading
    }
    return `${heading}\n\n${body}`
  }

  let heading: string
  if (section.kind === 'custom') {
    heading =
      section.title.trim().length > 0
        ? `## ${section.title}`
        : `## ${section.sectionId}`
  } else if (section.title.trim().length > 0) {
    heading = `## ${section.sectionId} ${section.title}`
  } else {
    heading = `## ${section.sectionId}`
  }
  const body = section.markdown.trimEnd()
  if (body.length === 0) {
    return heading
  }
  return `${heading}\n\n${body}`
}

/**
 * Compose full document markdown from preamble + ordered section records.
 */
export function composeInstructionHandbookMarkdown(
  preamble: string,
  sections: InstructionHandbookSectionRecord[]
): string {
  const sortedSections = [...sections].sort(
    (left, right) => left.sortOrder - right.sortOrder
  )
  const parts: string[] = []
  const trimmedPreamble = preamble.trimEnd()
  if (trimmedPreamble.length > 0) {
    parts.push(trimmedPreamble)
  }

  for (const section of sortedSections) {
    const block = formatInstructionHandbookSectionBlock(section)
    if (block.trim().length === 0) {
      continue
    }
    if (parts.length > 0) {
      parts.push('')
    }
    parts.push(block)
  }

  return parts.join('\n')
}
